/**
 * 인증 및 사용자 관련 API
 */

import { apiCall, apiFetch, publicApiCall } from "./base";
import type { StudioPolicy } from "./access";

export type EmailPasswordPrecheckAccountType =
  "unregistered" | "password" | "google" | "microsoft" | "apple" | "registered";

export interface EmailPasswordPrecheckResponse {
  accountType: EmailPasswordPrecheckAccountType;
  requestStatus: "approved" | "pending" | "rejected" | "not_requested";
  assignedProvider:
    "unresolved" | "password" | "google" | "microsoft" | "apple" | null;
  nextAction:
    | "password_login"
    | "social_login"
    | "request_pending"
    | "request_access"
    | "access_approved"
    | "contact_admin";
  requestId: string | null;
  email: string;
  tenantId: string | null;
  tenantName: string | null;
  authType: "company" | "social" | null;
  policy: StudioPolicy;
}

/**
 * Login 전에 Firebase identity의 provider category만 확인한다.
 * Password는 이 요청으로 전송하지 않는다.
 */
export const precheckEmailPasswordAccount = async (
  email: string,
  options: Pick<RequestInit, "signal"> = {},
): Promise<EmailPasswordPrecheckResponse> =>
  publicApiCall("/api/auth/email-password/precheck", {
    method: "POST",
    body: JSON.stringify({ email }),
    ...options,
  }) as Promise<EmailPasswordPrecheckResponse>;

/**
 * 사용자 정보 저장 (Google 로그인 후)
 */
export const saveUserInfo = async (
  userInfo: unknown,
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(userInfo),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  try {
    return await apiCall("/api/users/save", options);
  } catch (error) {
    const err = error as Error;
    console.error("❌ /api/users/save 엔드포인트 실패:", error);

    // 404 오류인 경우 구버전 경로 시도
    if (err.message.includes("404")) {
      try {
        const fallbackResponse = await apiCall("/users/save", options);
        return fallbackResponse;
      } catch (fallbackError) {
        console.error("❌ 구버전 경로도 실패:", fallbackError);
        throw error; // 원래 오류를 다시 던짐
      }
    }

    // 500 오류인 경우 대체 엔드포인트 시도
    if (err.message.includes("500")) {
      try {
        const fallbackResponse = await apiCall("/users/save-test", options);
        return fallbackResponse;
      } catch (fallbackError) {
        console.error("❌ 대체 엔드포인트도 실패:", fallbackError);
        throw error; // 원래 오류를 다시 던짐
      }
    }

    throw error;
  }
};

/**
 * 사용자 정보 조회
 */
export const getUserInfo = async (
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };

  try {
    // 먼저 /api/users/me 시도
    return await apiCall("/api/users/me", options);
  } catch (error) {
    const err = error as Error;
    // 404 오류인 경우 구버전 경로 시도
    if (err.message.includes("404")) {
      try {
        return await apiCall("/users/me", options);
      } catch (fallbackError) {
        // 둘 다 실패하면 null 반환 (차단 확인 불가 = 허용)
        console.warn(
          "⚠️ getUserInfo: 사용자 정보 조회 엔드포인트 없음, 차단 확인 건너뜀",
        );
        return null;
      }
    }
    throw error;
  }
};

const COMMENT_PROFILE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

/**
 * 현재 인증 사용자의 provider profile image를 Main API를 통해 가져옵니다.
 * Client가 upstream URL이나 다른 user identity를 지정하지 않는 current-user 전용 계약입니다.
 */
export const getCurrentUserProfileImage = async (): Promise<Blob> => {
  const response = await apiFetch("/api/users/user/profile?content=image", {
    method: "GET",
    headers: { Accept: "image/png,image/jpeg,image/webp" },
  });
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "profile_image_unavailable"
        : "profile_image_fetch_failed",
    );
  }

  const contentType = (response.headers.get("content-type") ?? "")
    .split(";", 1)[0]
    .trim()
    .toLowerCase();
  if (!COMMENT_PROFILE_IMAGE_TYPES.has(contentType)) {
    throw new Error("profile_image_invalid_type");
  }
  return response.blob();
};

/**
 * 모든 사용자 목록 조회 (관리자용)
 */
export const getAllUsers = async (
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };

  return await apiCall("/api/users/all", options);
};

/**
 * 사용자 접속 차단
 */
export const blockUser = async (
  userId: string,
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(`/api/users/${userId}/block`, options);
};

/**
 * 사용자 접속 차단 해제
 */
export const unblockUser = async (
  userId: string,
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(`/api/users/${userId}/unblock`, options);
};

/**
 * 이메일로 사용자 접속 차단 (같은 이메일의 모든 계정 차단)
 */
export const blockUserByEmail = async (
  email: string,
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(
    `/api/users/block-by-email?email=${encodeURIComponent(email)}`,
    options,
  );
};

/**
 * 이메일로 사용자 접속 차단 해제 (같은 이메일의 모든 계정 차단 해제)
 */
export const unblockUserByEmail = async (
  email: string,
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(
    `/api/users/unblock-by-email?email=${encodeURIComponent(email)}`,
    options,
  );
};

/**
 * 계정 삭제
 */
export const deleteAccount = async (
  payload: unknown = {},
  userToken: string | null,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "DELETE",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  try {
    return await apiCall("/api/users/me", options);
  } catch (error) {
    const err = error as Error;
    if (err.message.includes("404")) {
      return await apiCall("/users/me", options);
    }
    throw error;
  }
};

/**
 * Unity integration - get fresh Firebase token with automatic refresh
 */
export const getIdToken = async (): Promise<string> => {
  const { ensureValidToken } = await import("./base");
  // ✅ ensureValidToken 사용 (항상 유효한 토큰 보장)
  return await ensureValidToken();
};
